<template>
    <div class="page-container">
        <div class="container-fluid">
            <!-- Navigation Breadcrumb -->
            <div class="page-navigation">
                <div class="breadcrumb-custom">
                    <router-link to="/">
                        <i class="fas fa-home"></i> Home
                    </router-link>
                    <span class="separator">/</span>
                    <router-link to="/attendance">Kehadiran</router-link>
                    <span class="separator">/</span>
                    <router-link :to="{ name: 'TamhidiAttendanceSubject', params: { semester: $route.params.semester, year: $route.params.year } }">Mata Kuliah</router-link>
                    <span class="separator">/</span>
                    <span>Detail Tamhidi</span>
                </div>
            </div>

            <!-- Page Header -->
            <div class="page-header">
                <h2 class="page-title">Detail Kehadiran Tamhidi</h2>
                <p class="page-subtitle">Rekod kehadiran harian program Tamhidi</p>
            </div>

            <div class="row justify-content-center">
                <div class="col-12 col-lg-6">
                    
                    <!-- Subject Info Card -->
                    <div class="info-card mb-4" v-if="subject">
                        <div class="info-header">
                            <i class="fas fa-book"></i>
                            <h5>{{ getSubjectTitle() }}</h5>
                        </div>
                        <div class="info-body">
                            <div class="info-grid">
                                <div class="info-item">
                                    <span class="info-label">Pensyarah</span>
                                    <span class="info-value">{{ subject.teacher_name || '-' }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Tingkatan</span>
                                    <span class="info-value">{{ subject.level }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Semester</span>
                                    <span class="info-value">{{ subject.semester }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">Tahun</span>
                                    <span class="info-value">{{ subject.year }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Summary Card -->
                    <div class="summary-card mb-4" v-if="summary">
                        <div class="summary-header">
                            <i class="fas fa-chart-pie"></i>
                            <h6>Ringkasan Kehadiran</h6>
                        </div>
                        <div class="summary-body">
                            <div class="summary-stats">
                                <div class="stat-box primary">
                                    <span class="stat-number">{{ summary.total }}</span>
                                    <span class="stat-label">Jumlah Hisah</span>
                                </div>
                                <div class="stat-box success">
                                    <span class="stat-number">{{ summary.hadir }}</span>
                                    <span class="stat-label">Hadir</span>
                                </div>
                                <div class="stat-box danger">
                                    <span class="stat-number">{{ summary.ghaib }}</span>
                                    <span class="stat-label">Ghaib</span>
                                </div>
                                <div class="stat-box warning">
                                    <span class="stat-number">{{ summary.uzur }}</span>
                                    <span class="stat-label">Uzur</span>
                                </div>
                            </div>
                            <div class="percentage-display" :class="summary.percent >= 60 ? 'success' : 'danger'">
                                <i :class="summary.percent >= 60 ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                                Persentase Kehadiran: <strong>{{ summary.percent }}%</strong>
                            </div>
                        </div>
                    </div>

                    <!-- Attendance Detail Table -->
                    <div class="detail-card">
                        <div class="detail-header">
                            <i class="fas fa-calendar-check"></i>
                            <h6>Senarai Kehadiran</h6>
                        </div>
                        <div class="detail-body">
                            <div class="table-responsive">
                                <table class="attendance-table">
                                    <thead>
                                        <tr>
                                            <th width="10%">No</th>
                                            <th width="50%">Tarikh</th>
                                            <th width="40%">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in attendances" :key="item.teaching_history_id">
                                            <td class="text-center">{{ index + 1 }}</td>
                                            <td>{{ formatDate(item.teaching_date) }}</td>
                                            <td>
                                                <span class="attendance-badge" :class="getStatusClass(item.attendance_status)">
                                                    <i :class="getStatusIcon(item.attendance_status)"></i>
                                                    {{ item.status_text }}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-if="attendances.length === 0 && !loading">
                                            <td colspan="3" class="empty-row">
                                                <i class="fas fa-inbox"></i>
                                                Tiada rekod kehadiran dijumpai
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Loading Spinner -->
                    <div v-if="loading" class="loading-state">
                        <div class="loading-spinner"></div>
                        <p>Memuat data...</p>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from '@/services/BackendService'

export default {
    name: 'TamhidiAttendanceDetail',
    mounted() {
        this.retrieveAttendanceDetail()
    },
    data() {
        return {
            subject: null,
            attendances: [],
            summary: null,
            loading: false
        }
    },
    methods: {
        getSubjectTitle() {
            if (!this.subject) return ''
            return `${this.subject.subject || 'Subject'} - Tingkatan ${this.subject.level}`
        },
        retrieveAttendanceDetail() {
            this.loading = true
            this.$Progress.start()
            
            const groupId = this.$route.params.mustawa_group_id
            
            http.get(`tamhidi/attendanceDetail/${groupId}`)
                .then(response => {
                    this.subject = response.data.subject
                    this.attendances = response.data.attendances || []
                    this.summary = response.data.summary
                    this.loading = false
                    this.$Progress.finish()
                })
                .catch(error => {
                    this.loading = false
                    this.$Progress.fail()
                    console.error('Error fetching attendance detail:', error)
                    if (error.response && (error.response.status == 401 || error.response.status == 419)) {
                        this.$store.dispatch("authentication/cencelAuthen")
                    }
                })
        },
        formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}/${month}/${year}`
        },
        getStatusClass(status) {
            const s = status === null || status === undefined ? '' : String(status)
            switch(s) {
                case '1': return 'success'
                case '3': return 'danger'
                case '5': return 'warning'
                default: return 'secondary'
            }
        },
        getStatusIcon(status) {
            const s = status === null || status === undefined ? '' : String(status)
            switch(s) {
                case '1': return 'fas fa-check-circle'
                case '3': return 'fas fa-times-circle'
                case '5': return 'fas fa-exclamation-circle'
                default: return 'fas fa-question-circle'
            }
        }
    }
}
</script>

<style scoped>
/* Info Card */
.info-card {
    background: var(--bg-white);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-md);
    overflow: hidden;
}

.info-header {
    background: var(--primary-gradient);
    color: var(--text-white);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.info-header i {
    font-size: 1.2rem;
}

.info-header h5 {
    margin: 0;
    font-weight: 600;
}

.info-body {
    padding: 20px;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.info-item {
    display: flex;
    flex-direction: column;
}

.info-label {
    font-size: 0.8rem;
    color: var(--text-secondary);
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-value {
    font-weight: 600;
    color: var(--text-primary);
}

/* Summary Card */
.summary-card {
    background: var(--bg-white);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-md);
    overflow: hidden;
}

.summary-header {
    background: #6c757d;
    color: var(--text-white);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.summary-header h6 {
    margin: 0;
    font-weight: 600;
}

.summary-body {
    padding: 20px;
}

.summary-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 20px;
}

.stat-box {
    text-align: center;
    padding: 16px 12px;
    border-radius: var(--border-radius-sm);
}

.stat-box.primary {
    background: rgba(45, 106, 79, 0.1);
}

.stat-box.success {
    background: rgba(71, 192, 122, 0.15);
}

.stat-box.danger {
    background: rgba(220, 53, 69, 0.1);
}

.stat-box.warning {
    background: rgba(255, 193, 7, 0.15);
}

.stat-number {
    display: block;
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--text-primary);
}

.stat-box.success .stat-number { color: var(--primary-color); }
.stat-box.danger .stat-number { color: #dc3545; }
.stat-box.warning .stat-number { color: #b8860b; }

.stat-label {
    font-size: 0.8rem;
    color: var(--text-secondary);
}

.percentage-display {
    text-align: center;
    padding: 16px;
    border-radius: var(--border-radius-sm);
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.percentage-display.success {
    background: rgba(71, 192, 122, 0.15);
    color: var(--primary-color);
}

.percentage-display.danger {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
}

/* Detail Card */
.detail-card {
    background: var(--bg-white);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-md);
    overflow: hidden;
}

.detail-header {
    background: var(--primary-gradient);
    color: var(--text-white);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.detail-header h6 {
    margin: 0;
    font-weight: 600;
}

.detail-body {
    padding: 0;
}

.attendance-table {
    width: 100%;
    border-collapse: collapse;
}

.attendance-table thead {
    background: #f8f9fa;
}

.attendance-table th {
    padding: 14px 16px;
    text-align: left;
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e9ecef;
}

.attendance-table td {
    padding: 14px 16px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 0.95rem;
}

.attendance-table tbody tr:hover {
    background: #f8f9fa;
}

.attendance-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 20px;
    font-weight: 500;
    font-size: 0.85rem;
}

.attendance-badge.success {
    background: rgba(71, 192, 122, 0.15);
    color: var(--primary-color);
}

.attendance-badge.danger {
    background: rgba(220, 53, 69, 0.15);
    color: #dc3545;
}

.attendance-badge.warning {
    background: rgba(255, 193, 7, 0.15);
    color: #b8860b;
}

.attendance-badge.secondary {
    background: #e9ecef;
    color: #6c757d;
}

.empty-row {
    text-align: center;
    padding: 40px 20px !important;
    color: var(--text-secondary);
}

.empty-row i {
    display: block;
    font-size: 2rem;
    margin-bottom: 12px;
    opacity: 0.4;
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 40px;
    color: var(--text-secondary);
}

.loading-state p {
    margin-top: 12px;
}

@media (max-width: 768px) {
    .info-grid {
        grid-template-columns: 1fr;
    }
    
    .summary-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .attendance-table th,
    .attendance-table td {
        padding: 10px 12px;
        font-size: 0.85rem;
    }
}
</style>
